import React, { useEffect } from 'react';
import { InvoiceRecord } from '@/types/domain/invoice-management';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import { Database, RefreshCw, X, ShieldCheck } from 'lucide-react';

interface InvoiceSapSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSync: () => Promise<void>;
  invoice: InvoiceRecord;
  isSubmitting?: boolean;
}

export const InvoiceSapSyncModal: React.FC<InvoiceSapSyncModalProps> = ({
  isOpen,
  onClose,
  onConfirmSync,
  invoice,
  isSubmitting = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        onClick={() => !isSubmitting && onClose()}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-xl rounded-2xl p-6 bg-card border border-border shadow-2xl z-10 space-y-4"
      >
        <div className="flex items-start justify-between border-b border-border/60 pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-600">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-foreground">
                SAP S/4HANA Invoice Verification (MIRO BAPI)
              </h3>
              <p className="text-xs text-foreground-secondary">
                Post official vendor invoice document into SAP ERP central finance ledger.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-foreground-muted hover:text-foreground p-1 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-3.5 rounded-xl border border-border/70 bg-surface/50 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">ProcureAI Invoice #:</span>
            <span className="font-extrabold text-primary font-mono">{invoice.invoiceNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Vendor Invoice #:</span>
            <span className="font-bold text-foreground font-mono">{invoice.vendorInvoiceNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Supplier / Vendor:</span>
            <span className="font-bold text-foreground">{invoice.vendorName} ({invoice.vendorCode})</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Linked PO Document #:</span>
            <span className="font-mono text-purple-700 font-bold">{invoice.poNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Invoice Total:</span>
            <span className="font-mono font-black text-foreground">
              {formatCurrency(invoice.totalAmount, invoice.currency)}
            </span>
          </div>
        </div>

        <div className="p-3 rounded-xl border border-border/80 bg-slate-900 text-slate-100 font-mono text-[11px] overflow-x-auto space-y-1.5">
          <span className="text-slate-400 text-[10px] uppercase font-bold block">// BAPI_INCOMINGINVOICE_CREATE Payload</span>
          <pre className="leading-relaxed">
{JSON.stringify(
  {
    HEADERDATA: {
      INVOICE_IND: 'X',
      DOC_TYPE: 'RE',
      DOC_DATE: invoice.invoiceDate,
      PSTNG_DATE: invoice.receivedDate,
      REF_DOC_NO: invoice.vendorInvoiceNumber,
      COMP_CODE: invoice.companyCode,
      DIFF_INV: invoice.vendorCode,
      CURRENCY: invoice.currency,
      GROSS_AMOUNT: invoice.totalAmount,
    },
    ITEMDATA: invoice.items.map((it) => ({
      INVOICE_DOC_ITEM: it.lineNumber,
      PO_NUMBER: invoice.poNumber,
      PO_ITEM: it.lineNumber,
      REF_DOC: invoice.grnNumber || '',
      ITEM_AMOUNT: it.lineTotal,
      QUANTITY: it.quantity,
      PO_UNIT: it.uom,
    })),
  },
  null,
  2
)}
          </pre>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border/40">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting} className="rounded-xl font-bold">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onConfirmSync}
            disabled={isSubmitting}
            className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-2xs"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Posting to SAP MIRO...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4" /> Post SAP MIRO Document
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
